import React from 'react';
import {
	FromTitle,
	ObjectBrowserLinkBuilder,
	objectBrowserStoreSelector,
	useObjectBrowserStore,
} from '@warehouse/object-browser/domain';
import { Breadcrumbs } from '@warehouse/shared/ui';
import { StoreResourceIdentifier } from '@warehouse/object-browser/core';
import styled, { css } from 'styled-components';
import { Box } from '@mui/material';
import { useExternalIntegrations } from '../../../../src/utils/hooks/useExternalIntegrations';
import { AsperaUploadButton } from './AsperaUploadButton';
import { BucketIcon, FolderIcon } from './icons';
import { FileIcon } from './icons/file';

export function ObjectBrowserBreadcrumbs() {
	const breadcrumbsItems = useObjectBrowserStore(objectBrowserStoreSelector.breadcrumbsItems) || [];
	const isBreadcrumbsItemsPending = useObjectBrowserStore(objectBrowserStoreSelector.isBreadcrumbsItemsPending);
	const showBreadcrumb = useObjectBrowserStore(objectBrowserStoreSelector.showBreadcrumb);
	const status = useObjectBrowserStore(objectBrowserStoreSelector.status);
	const title = useObjectBrowserStore(objectBrowserStoreSelector.title);
	const actions = useObjectBrowserStore(objectBrowserStoreSelector.actions);
	const buckets = useObjectBrowserStore(objectBrowserStoreSelector.buckets);
	const externalIntegrations = useExternalIntegrations();

	if (!showBreadcrumb) return null;

	const handlePathUse = (path: { type: string; label: string }[] | []) =>
		path
			?.filter((item: { type: string; label: string }) => item?.type === 'folder')
			?.map((item: { type: string; label: string }) => item.label)
			?.join('/');

	return (
		<BreadcrumbsWrapper>
			<Box display="flex" alignItems="flex-start" justifyContent="space-between" minHeight="32px" gap={2}>
				<Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
					<Breadcrumbs
						maxItems={5}
						loading={isBreadcrumbsItemsPending}
						items={breadcrumbsItems.map((item) => ({
							to: (() => {
								if (title && !title.readonly.titleSearchDisplayNameFull)
									console.error(`titleSearchDisplayNameFull is missing in title ${title.uuid}`);
								return getItemTo(
									item.resourceIdentifier,
									title
										? {
												licensorLabel: title.readonly.licensor.label,
												// titleSearchDisplayNameFull not being available is very edge case, and in that rare case we are fine
												// filtering on empty string and showing all the titles
												titleSearch: title.readonly.titleSearchDisplayNameFull ?? '',
											}
										: undefined,
								);
							})(),
							label: item.label,
							icon: getItemIcon(item.type),
							onClick:
								status === 'title-objects-tab-ready'
									? undefined
									: () => {
											if (item.resourceIdentifier) actions.setResourceIdentifier(item.resourceIdentifier);
											// If we are on search mode, clicking on breadcrumb will clear the search query
											actions.setSearchQuery('');
										},
						}))}
						openNewTab={status === 'title-objects-tab-ready'}
					/>
				</Box>
				<RenderAsperaUploadButton
					breadcrumbsItems={breadcrumbsItems}
					buckets={buckets}
					externalIntegrations={externalIntegrations}
					handlePathUse={handlePathUse}
				/>
			</Box>
		</BreadcrumbsWrapper>
	);
}

// Separate logic for rendering the AsperaUploadButton conditionally
function RenderAsperaUploadButton({
	breadcrumbsItems,
	buckets,
	externalIntegrations = [],
	handlePathUse,
}: Readonly<{
	breadcrumbsItems: { label: string; type: string }[];
	buckets: { id: string; isManaged: boolean }[];
	externalIntegrations: { id: string; name: string; serviceURL: string }[];
	handlePathUse: (path: { type: string; label: string }[]) => string;
}>) {
	const bucket = breadcrumbsItems.find((item) => item.type === 'bucket');
	const bucketInfo = buckets.find((b) => b?.id === bucket?.label);
	const aspera = externalIntegrations.find((integration: { id: string }) => integration.id === 'aspera');
	if (!bucketInfo?.isManaged || !aspera) return null;

	const path = handlePathUse(breadcrumbsItems);
	return <AsperaUploadButton currentBucketPath={path} bucketInfo={bucketInfo} />;
}

function getItemTo(resourceIdentifier: StoreResourceIdentifier | undefined, fromTitle?: FromTitle) {
	if (!resourceIdentifier) return undefined;

	const titleUuidArgs = fromTitle
		? {
				forwardSearchParams: false,
				fromTitle,
			}
		: {
				forwardSearchParams: true,
			};

	if (resourceIdentifier?.type === 'bucket') {
		return ObjectBrowserLinkBuilder.buildBucketLink({ bucketId: resourceIdentifier.bucketId, ...titleUuidArgs });
	}
	if (resourceIdentifier?.type === 'folderUuid') {
		return ObjectBrowserLinkBuilder.buildFolderLink({ folderUuid: resourceIdentifier.folderUuid, ...titleUuidArgs });
	}
	return undefined;
}

function getItemIcon(type: 'bucket' | 'folder' | 'file') {
	switch (type) {
		case 'bucket':
			return <BucketIcon />;
		case 'folder':
			return <FolderIcon />;
		case 'file':
			return <FileIcon />;
		default:
			return undefined;
	}
}

const BreadcrumbsWrapper = styled.div(
	({ theme }) => css`
		border-bottom: solid 1px ${theme.palette.light.backgroundAlt};
		padding: 8px 20px;
	`,
);
